<script setup>
import { ref, watch, computed } from "vue";
import { ElMessage } from "element-plus";
import { switchRoleApi } from "@/api/sysadmin";
import { setToken } from "@/util/common";
import { useUserStore } from "@/store/user";
import { enumConfig } from "@/util/enum/index.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  // 是否来自登录页面（多角色首次进入选择场景）
  fromLogin: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:visible", "confirm"]);

const userStore = useUserStore();
const userInfo = ref(userStore.getUserInfo());
const activeRoleValue = ref(userStore.getActiveRoleValue());

const dialogVisible = ref(false);
const loading = ref(false);
// 当前选中的角色值
const selectedRole = ref(activeRoleValue.value);

// 可切换的身份列表
const roleList = computed(() => userInfo.value?.identities || []);

watch(() => props.visible, (val) => {
  dialogVisible.value = val;
  if (val) {
    // 打开时重新读取最新用户信息（登录场景下用户信息在打开前才写入）
    userInfo.value = userStore.getUserInfo();
    activeRoleValue.value = userStore.getActiveRoleValue();
    selectedRole.value = activeRoleValue.value;
  }
}, { immediate: true });

watch(dialogVisible, (val) => {
  emit("update:visible", val);
});

const handleCancel = () => {
  dialogVisible.value = false;
};

const handleConfirm = async () => {
  if (selectedRole.value == null || selectedRole.value === "") {
    ElMessage.warning("请选择要进入的角色");
    return;
  }
  // 选择的角色与当前一致时，登录场景下直接进入，其余场景提示
  if (selectedRole.value === activeRoleValue.value) {
    if (props.fromLogin) {
      emit("confirm");
      localStorage.setItem('hasSwitchRole', true)    
      dialogVisible.value = false;
      window.location.href = "/";

      return;
    }
    ElMessage.warning("当前已是该角色");
    return;
  }
  loading.value = true;
  try {
    const res = await switchRoleApi({ activeRoleValue: selectedRole.value });
    if (res.code === 0) {
      const resData = res.data || {};
      if (resData.accessToken) {
        setToken(resData.accessToken);
      }
      resData.userType = resData.activeRoleValue || selectedRole.value;
      // 合并原用户信息，保留身份列表等字段
      userStore.setUserInfo({ ...resData });
      // 清除路由，下次访问时重新加载
      userStore.routes = [];
      localStorage.setItem('hasSwitchRole', true)    
      ElMessage.success("角色切换成功");
      emit("confirm");
      dialogVisible.value = false;
      // 跳转首页并重新加载菜单
      window.location.href = "/";
    } else {
      ElMessage.error(res.msg || "角色切换失败");
    }
  } catch (err) {
    console.error("角色切换失败", err);
    ElMessage.error("角色切换失败");
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="fromLogin ? '请选择进入角色' : '切换角色'"
    width="4.6rem"
    :close-on-click-modal="false"
    :close-on-press-escape="!fromLogin"
    :show-close="!fromLogin"
    destroy-on-close
  >
  <div class="role-select-container">
    <el-select v-model="selectedRole" placeholder="请选择要进入的角色" class="role-select">
      <el-option
        v-for="item in roleList"
        :key="item.roleTypeValue"
        :label="item.roleTypeName"
        :value="item.roleTypeValue"
      />
    </el-select>
    </div>

    <template #footer>
      <el-button v-if="!fromLogin" :plain="true" @click="handleCancel">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">
        {{ fromLogin ? "进入" : "确定切换" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.role-select-container{
  padding: 0.2rem;
}
.role-select {
  width: 100%;
}
</style>
